import { motion, AnimatePresence } from 'framer-motion';
import { 
  Play, 
  Smartphone, 
  Sparkles, 
  CheckCircle2, 
  Trophy, 
  BookOpen, 
  ChevronRight, 
  Clock, 
  Star,
  Users,
  LineChart,
  Menu,
  X,
  GraduationCap,
  ArrowRight
} from 'lucide-react';
import { useState } from 'react';
import logo from '../assets/logo.png';
import AntiGravityHero from '../components/AntiGravityHero';

interface LandingPageProps {
  onLoginClick: () => void;
  onBecomeInstructor?: () => void;
}

export default function LandingPage({ onLoginClick, onBecomeInstructor }: LandingPageProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Auto scroll effects for parallax appearance
  const fadeInUp: any = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
  };

  return (
    <div className="bg-[#080808] text-white min-h-screen font-bangla relative overflow-x-hidden">
      
      {/* 1. Public Translucent Navigation Header */}
      <header className="sticky top-0 left-0 right-0 z-40 bg-[#080808]/80 backdrop-blur-md border-b border-white/5 transition-all">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-20 flex items-center justify-between">
          {/* Logo brand */}
          <div className="flex items-center gap-3">
            <img src={logo} alt="শুদ্ধশিক্ষা" className="w-12 h-12 rounded-xl object-cover" />
            <div className="flex flex-col text-left">
              <h1 className="text-lg font-extrabold leading-none text-white tracking-wide">শুদ্ধশিক্ষা</h1>
              <span className="text-[10px] text-gray-400 font-medium tracking-widest mt-0.5 uppercase">শেখা হোক সহজ</span>
            </div>
          </div>

          {/* Nav links (Desktop) */}
          <nav className="hidden md:flex items-center gap-8 text-sm font-semibold text-gray-400">
            <a href="#features" className="hover:text-emerald-400 transition-colors">ফিচারসমূহ</a>
            <a href="#highlights" className="hover:text-emerald-400 transition-colors">চর্চা প্যানেল</a>
            <a href="#success" className="hover:text-emerald-400 transition-colors">চর্চার সাফল্য</a>
            <a href="#contact" className="hover:text-emerald-400 transition-colors">যোগাযোগ</a>
          </nav>

          {/* Action CTAs & Menu */}
          <div className="flex items-center gap-2 sm:gap-4">
            <button
              onClick={onLoginClick}
              className="px-3 py-1.5 sm:px-5 sm:py-2.5 bg-[#00CBA9] hover:bg-[#00b092] text-black text-xs font-black rounded-full shadow-lg shadow-[#00CBA9]/15 active:scale-95 transition-all cursor-pointer whitespace-nowrap"
            >
              লগইন / সাইন আপ
            </button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-xl bg-white/5 border border-white/10 text-gray-400 hover:text-white cursor-pointer"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu dropdown */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-[#0C0C0C] border-b border-white/5 overflow-hidden text-left"
            >
              <div className="px-6 py-4 space-y-4 flex flex-col font-semibold text-gray-400">
                <a href="#features" onClick={() => setMobileMenuOpen(false)} className="hover:text-[#00CBA9]">ফিচারসমূহ</a>
                <a href="#highlights" onClick={() => setMobileMenuOpen(false)} className="hover:text-[#00CBA9]">চর্চা প্যানেল</a>
                <a href="#success" onClick={() => setMobileMenuOpen(false)} className="hover:text-[#00CBA9]">চর্চার সাফল্য</a>
                <a href="#contact" onClick={() => setMobileMenuOpen(false)} className="hover:text-[#00CBA9]">যোগাযোগ</a>
                <div className="h-px bg-white/5 pt-1" />
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    onLoginClick();
                  }}
                  className="w-full py-3 bg-[#00CBA9] text-black font-extrabold text-xs rounded-xl text-center cursor-pointer shadow-lg shadow-[#00CBA9]/10"
                >
                  লগইন / সাইন আপ
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* 2. Hero Section */}
      <section className="relative pt-12 pb-24 md:py-32 overflow-hidden px-4 md:px-8 max-w-7xl mx-auto">
        {/* Colorful neon glows in background (Optimized: radial-gradients avoid paint-intensive CSS blurs) */}
        <div className="absolute top-1/4 -right-20 w-96 h-96 bg-[radial-gradient(circle_at_center,rgba(147,51,234,0.08),transparent_70%)] pointer-events-none" />
        <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-[radial-gradient(circle_at_center,rgba(16,185,129,0.04),transparent_70%)] pointer-events-none" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 md:gap-16 items-center">
          {/* Left Text Column */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 text-left space-y-6 md:space-y-8"
          >
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-white/5 border border-white/10 rounded-full text-emerald-400 font-bangla text-xs font-bold uppercase tracking-wide">
              <Sparkles className="w-4 h-4 text-emerald-400" />
              এসএসসি ও এইচএসসি প্রস্তুতি
            </div>

            <h2 className="text-4xl md:text-6xl font-black leading-tight text-white tracking-tight">
              চর্চা করো নিজের <br className="hidden sm:inline" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00CBA9] via-[#00A3FF] to-purple-400">
                গতিতে ও স্মার্টলি
              </span>
            </h2>

            <p className="text-base md:text-lg text-gray-400 max-w-xl font-normal leading-relaxed">
              অধ্যায় ভিত্তিক কুইজ, পূর্ণাঙ্গ মক টেস্ট ও রিয়েল-টাইম লিডারবোর্ডের মাধ্যমে নিজেকে যাচাই করুন। হাজারো শিক্ষার্থীর সাথে প্রতিযোগিতায় অংশ নিয়ে প্রস্তুতিকে করুন নিখুঁত।
            </p>

            {/* Badges and download links */}
            <div className="flex flex-wrap gap-4 pt-2">
              <a href="#playstore" className="hover:opacity-90 transition-opacity">
                <div className="bg-[#121212] hover:bg-[#1A1A1A] border border-white/10 rounded-xl px-4 py-2.5 flex items-center gap-3 text-left w-40 cursor-pointer">
                  <Smartphone className="w-7 h-7 text-emerald-400" />
                  <div>
                    <p className="text-[9px] uppercase tracking-wider text-gray-500 font-bold leading-none">GET IT ON</p>
                    <p className="text-xs text-white font-bold leading-none mt-1 font-english">Google Play</p>
                  </div>
                </div>
              </a>
              <a href="#appstore" className="hover:opacity-90 transition-opacity">
                <div className="bg-[#121212] hover:bg-[#1A1A1A] border border-white/10 rounded-xl px-4 py-2.5 flex items-center gap-3 text-left w-40 cursor-pointer">
                  <Play className="w-7 h-7 text-[#00A3FF] fill-current" />
                  <div>
                    <p className="text-[9px] uppercase tracking-wider text-gray-500 font-bold leading-none">Download on</p>
                    <p className="text-xs text-white font-bold leading-none mt-1 font-english">App Store</p>
                  </div>
                </div>
              </a>
            </div>

            {/* Quick Metrics */}
            <div className="grid grid-cols-3 gap-6 pt-6 border-t border-white/5 max-w-md">
              <div>
                <p className="text-2xl font-black text-white">১০০K+</p>
                <p className="text-xs text-gray-400 mt-1 font-medium">ডাউনলোডস</p>
              </div>
              <div>
                <p className="text-2xl font-black text-[#00CBA9]">৪.৯★</p>
                <p className="text-xs text-gray-400 mt-1 font-medium">প্লে স্টোর রেটিং</p>
              </div>
              <div>
                <p className="text-2xl font-black text-[#00A3FF]">৫০M+</p>
                <p className="text-xs text-gray-400 mt-1 font-medium">কুইজ সম্পন্ন</p>
              </div>
            </div>
          </motion.div>

          {/* Right Mockup Column - Anti-Gravity Hero Section */}
          <motion.div 
            initial={{ opacity: 0, x: 30, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5 relative flex justify-center w-full"
          >
            <AntiGravityHero />
          </motion.div>
        </div>
      </section>

      {/* 3. Social Proof Section ("চর্চার সাথে পড়াশোনা হবে স্মার্ট") */}
      <section id="highlights" className="py-24 border-t border-white/5 bg-[#0a0a0a] relative px-4 md:px-8">
        <div className="max-w-7xl mx-auto text-center space-y-16">
          <div className="space-y-4 max-w-2xl mx-auto">
            <h3 className="text-3xl md:text-4xl font-black leading-tight text-white">
              চর্চার সাথে পড়াশোনা হবে <br className="xs:hidden" />
              <span className="text-[#00CBA9]">স্মার্ট, ফান ও সহজ</span>
            </h3>
            <p className="text-sm text-gray-400 font-normal">
              অ্যাপের ইন্টারেক্টিভ কুইজ আর অ্যানালিটিক্স ব্যবহার করে দুর্বল বিষয়গুলো সহজেই খুঁজে বের করো।
            </p>
          </div>

          {/* Social Proof UI components grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
            
            {/* Widget 1: Progress card circular ring */}
            <motion.div 
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="p-6 rounded-3xl bg-white/5 border border-white/5 hover:border-emerald-500/30 transition-all flex flex-col justify-between text-left group"
            >
              <div className="flex justify-between items-center pb-4 border-b border-white/5 mb-6">
                <h4 className="font-extrabold text-sm text-white flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  আজকের প্রোগ্রেস
                </h4>
                <span className="text-[10px] text-emerald-400 font-bold bg-emerald-500/10 px-2 py-0.5 rounded-full">ACTIVE</span>
              </div>
              <div className="flex items-center gap-6">
                {/* Circle Ring */}
                <div className="relative w-24 h-24 flex items-center justify-center flex-shrink-0">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle cx="48" cy="48" r="38" stroke="#1F1F1F" strokeWidth="8" fill="transparent" />
                    <circle cx="48" cy="48" r="38" stroke="#00CBA9" strokeWidth="8" fill="transparent" strokeDasharray="238.7" strokeDashoffset="59.6" strokeLinecap="round" />
                  </svg>
                  <div className="absolute flex flex-col items-center justify-center font-english">
                    <span className="text-lg font-black text-white">৭৫%</span>
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-bold text-white">১৫টি কুইজ সম্পন্ন</p>
                  <p className="text-[10px] text-gray-400 font-medium">আজকের লক্ষ্য: ২০টি কুইজ</p>
                  <p className="text-[10px] text-yellow-500 font-bold flex items-center gap-0.5">১৫০+ এক্সপি অর্জন 🔥</p>
                </div>
              </div>
            </motion.div>

            {/* Widget 2: Leaderboard floating mockup item */}
            <motion.div 
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="p-6 rounded-3xl bg-white/5 border border-white/5 hover:border-blue-500/30 transition-all flex flex-col justify-between text-left group"
            >
              <div className="flex justify-between items-center pb-4 border-b border-white/5 mb-6">
                <h4 className="font-extrabold text-sm text-white flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-[#00A3FF]" />
                  সাপ্তাহিক লিডারবোর্ড
                </h4>
                <span className="text-[10px] text-[#00A3FF] font-bold bg-blue-500/10 px-2 py-0.5 rounded-full">RANKING</span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                  <div className="flex items-center gap-3">
                    <span className="font-black text-emerald-400">১</span>
                    <div className="w-6 h-6 rounded-full bg-slate-800 flex items-center justify-center text-white text-[9px] font-bold">সা</div>
                    <span className="font-bold text-xs text-white">সাব্বির রহমান</span>
                  </div>
                  <span className="font-black text-xs text-emerald-400">৪৫০০ XP</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-white/5 rounded-xl border border-white/5">
                  <div className="flex items-center gap-3">
                    <span className="font-black text-gray-400">২</span>
                    <div className="w-6 h-6 rounded-full bg-slate-800 flex items-center justify-center text-white text-[9px] font-bold">রা</div>
                    <span className="font-bold text-xs text-white">রাহুল আহমেদ (ইউ)</span>
                  </div>
                  <span className="font-black text-xs text-white">৪২০০ XP</span>
                </div>
              </div>
            </motion.div>

            {/* Widget 3: Analytical Line chart item */}
            <motion.div 
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="p-6 rounded-3xl bg-white/5 border border-white/5 hover:border-purple-500/30 transition-all flex flex-col justify-between text-left group"
            >
              <div className="flex justify-between items-center pb-4 border-b border-white/5 mb-6">
                <h4 className="font-extrabold text-sm text-white flex items-center gap-2">
                  <LineChart className="w-4 h-4 text-purple-400" />
                  সাপ্তাহিক উন্নতি গ্রাফ
                </h4>
                <span className="text-[10px] text-purple-400 font-bold bg-purple-500/10 px-2 py-0.5 rounded-full">STATS</span>
              </div>
              {/* Simulated mini Graph */}
              <div className="h-20 flex items-end justify-between gap-2 pt-2">
                {[45, 60, 55, 78, 90, 85, 98].map((val, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1.5 h-full justify-end">
                    <div 
                      className={`w-full rounded-t ${i === 6 ? 'bg-purple-500 shadow-[0_0_10px_rgba(168,85,247,0.4)]' : 'bg-white/10'}`} 
                      style={{ height: `${val}%` }} 
                    />
                    <span className="text-[8px] text-gray-500 font-bold uppercase">{['S','M','T','W','T','F','S'][i]}</span>
                  </div>
                ))}
              </div>
            </motion.div>

          </div>

          <button
            onClick={onLoginClick}
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#00CBA9] hover:bg-[#00b092] text-black font-bangla text-base font-black rounded-full shadow-xl shadow-[#00CBA9]/20 active:scale-[0.98] transition-all cursor-pointer border-none"
          >
            আজই শুরু করুন
            <ChevronRight className="w-5 h-5 stroke-[2.5]" />
          </button>
        </div>
      </section>

      {/* 4. Feature Showcase Grid (6 Features matching the image) */}
      <section id="features" className="py-24 max-w-7xl mx-auto px-4 md:px-8 relative">
        {/* Colorful neon glows in background (Optimized: radial-gradients avoid paint-intensive CSS blurs) */}
        <div className="absolute top-1/2 left-0 w-96 h-96 bg-[radial-gradient(circle_at_center,rgba(37,99,235,0.04),transparent_70%)] pointer-events-none" />
        
        <div className="text-center space-y-4 mb-20">
          <span className="text-[#00CBA9] text-xs font-bold uppercase tracking-widest bg-emerald-500/5 border border-emerald-500/10 px-3 py-1 rounded-full">
            চর্চার ফিচারসমূহ
          </span>
          <h3 className="text-3xl md:text-5xl font-black text-white">এক অ্যাপে সম্পূর্ণ প্রস্তুতি</h3>
          <p className="text-sm text-gray-400 font-normal max-w-xl mx-auto">
            আপনার প্রস্তুতিকে আরও গতিশীল ও পরিকল্পিত করতে চর্চায় রয়েছে ছয়টি আকর্ষণীয় ফিচার।
          </p>
        </div>

        {/* Feature Grid layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
          
          {/* Feature 1: অধ্যায় ভিত্তিক প্রস্তুতি */}
          <div className="bg-white/5 border border-white/5 hover:border-emerald-500/20 p-6 rounded-3xl flex flex-col justify-between items-center gap-6 hover:shadow-xl hover:shadow-[#00CBA9]/5 transition-all group">
            <div className="text-center space-y-2">
              <span className="inline-flex items-center justify-center p-3 rounded-2xl bg-emerald-500/10 text-emerald-400">
                <BookOpen className="w-6 h-6" />
              </span>
              <h4 className="font-extrabold text-base text-white pt-2">অধ্যায় ভিত্তিক প্রস্তুতি 📚</h4>
              <p className="text-xs text-gray-400 font-normal leading-relaxed">
                প্রতিটি সাবজেক্টের অধ্যায় অনুযায়ী সাজানো কুইজ সমাধান করে বেসিক শক্ত করুন।
              </p>
            </div>
            {/* Phone CSS Mockup 1 */}
            <div className="relative border-gray-800 bg-[#0d0d0d] border-[8px] rounded-[2rem] h-[340px] w-[170px] shadow-lg flex-shrink-0">
              <div className="w-[70px] h-[14px] bg-gray-900 top-1.5 rounded-[0.8rem] left-1/2 -translate-x-1/2 absolute z-20" />
              <div className="rounded-[1.6rem] overflow-hidden w-full h-full p-2.5 pt-7 text-[7px] text-left flex flex-col justify-between bg-[#080808]">
                <p className="font-black text-white border-b border-white/5 pb-1.5">পদার্থবিজ্ঞান ১ম পত্র</p>
                <div className="space-y-1.5 flex-1 pt-2">
                  {['ভেক্টর', 'গতিবিদ্যা', 'নিউটনীয় বলবিদ্যা', 'কাজ, শক্তি ও ক্ষমতা'].map((ch, idx) => (
                    <div key={idx} className="p-1.5 bg-white/5 border border-white/5 rounded-lg flex items-center justify-between text-white font-medium">
                      <span>{ch}</span>
                      <ChevronRight className="w-2.5 h-2.5 text-gray-500" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Feature 2: মক পরীক্ষা */}
          <div className="bg-white/5 border border-white/5 hover:border-purple-500/20 p-6 rounded-3xl flex flex-col justify-between items-center gap-6 hover:shadow-xl hover:shadow-purple-500/5 transition-all group">
            <div className="text-center space-y-2">
              <span className="inline-flex items-center justify-center p-3 rounded-2xl bg-purple-500/10 text-purple-400">
                <Trophy className="w-6 h-6" />
              </span>
              <h4 className="font-extrabold text-base text-white pt-2">পরীক্ষার মডিউল ⏳</h4>
              <p className="text-xs text-gray-400 font-normal leading-relaxed">
                রিয়েল-টাইম টাইমার সহ মক টেস্ট তৈরি করে নিখুঁত টাইম ম্যানেজমেন্টের প্রস্তুতি নিন।
              </p>
            </div>
            {/* Phone CSS Mockup 2 */}
            <div className="relative border-gray-800 bg-[#0d0d0d] border-[8px] rounded-[2rem] h-[340px] w-[170px] shadow-lg flex-shrink-0">
              <div className="w-[70px] h-[14px] bg-gray-900 top-1.5 rounded-[0.8rem] left-1/2 -translate-x-1/2 absolute z-20" />
              <div className="rounded-[1.6rem] overflow-hidden w-full h-full p-2.5 pt-7 text-[7px] text-left flex flex-col justify-between bg-[#080808]">
                <div className="flex justify-between items-center border-b border-white/5 pb-1.5">
                  <span className="font-black text-white">মডেল টেস্ট কুইজ</span>
                  <span className="text-red-500 font-black flex items-center"><Clock className="w-2.5 h-2.5 mr-0.5" /> ১৪:৫৯</span>
                </div>
                <div className="flex-1 pt-2 space-y-2">
                  <p className="font-bold text-white leading-tight">১. বলের মাত্রা সমীকরণ নিচের কোনটি?</p>
                  <div className="space-y-1 font-medium font-english">
                    {['MLT^-1', 'MLT^-2', 'ML^2T^-2', 'ML^2T^-1'].map((opt, i) => (
                      <div key={i} className={`p-1.5 rounded-lg border text-[6px] ${i === 1 ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-400' : 'border-white/5 bg-white/5 text-gray-300'}`}>
                        {['(ক)', '(খ)', '(গ)', '(ঘ)'][i]} {opt}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Feature 3: বিশ্লেষণ ও পারফরম্যান্স */}
          <div className="bg-white/5 border border-white/5 hover:border-blue-500/20 p-6 rounded-3xl flex flex-col justify-between items-center gap-6 hover:shadow-xl hover:shadow-blue-500/5 transition-all group">
            <div className="text-center space-y-2">
              <span className="inline-flex items-center justify-center p-3 rounded-2xl bg-blue-500/10 text-blue-400">
                <LineChart className="w-6 h-6" />
              </span>
              <h4 className="font-extrabold text-base text-white pt-2">বিশ্লেষণ ও রিপোর্ট 📊</h4>
              <p className="text-xs text-gray-400 font-normal leading-relaxed">
                আপনার সঠিক ও ভুল উত্তরের বিশ্লেষণের মাধ্যমে চিহ্নিত করুন আপনার দুর্বল দিকসমূহ।
              </p>
            </div>
            {/* Phone CSS Mockup 3 */}
            <div className="relative border-gray-800 bg-[#0d0d0d] border-[8px] rounded-[2rem] h-[340px] w-[170px] shadow-lg flex-shrink-0">
              <div className="w-[70px] h-[14px] bg-gray-900 top-1.5 rounded-[0.8rem] left-1/2 -translate-x-1/2 absolute z-20" />
              <div className="rounded-[1.6rem] overflow-hidden w-full h-full p-2.5 pt-7 text-[7px] text-left flex flex-col justify-between bg-[#080808]">
                <p className="font-black text-white border-b border-white/5 pb-1.5">পারফরম্যান্স রিপোর্ট</p>
                <div className="flex-1 pt-2 space-y-2">
                  <div className="p-2 bg-white/5 rounded-xl border border-white/5 space-y-1.5">
                    <p className="font-bold text-gray-400">সফলতার গড় হার</p>
                    <p className="text-lg font-black text-emerald-400">৮৫%</p>
                  </div>
                  <div className="p-2 bg-white/5 rounded-xl border border-white/5 space-y-1">
                    <p className="font-bold text-gray-400">সবচেয়ে দুর্বল বিষয়</p>
                    <p className="font-bold text-rose-400">জৈব রসায়ন (রসায়ন)</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Feature 4: বোর্ড ও স্কুল প্রশ্নব্যাংক */}
          <div className="bg-white/5 border border-white/5 hover:border-amber-500/20 p-6 rounded-3xl flex flex-col justify-between items-center gap-6 hover:shadow-xl hover:shadow-amber-500/5 transition-all group">
            <div className="text-center space-y-2">
              <span className="inline-flex items-center justify-center p-3 rounded-2xl bg-amber-500/10 text-amber-500">
                <Smartphone className="w-6 h-6" />
              </span>
              <h4 className="font-extrabold text-base text-white pt-2">বোর্ড ও স্কুল প্রশ্নব্যাংক 📝</h4>
              <p className="text-xs text-gray-400 font-normal leading-relaxed">
                বিগত বছরগুলোর বোর্ড পরীক্ষা ও সেরা বিদ্যালয়সমূহের টেস্ট পরীক্ষার প্রশ্ন সলভ করুন।
              </p>
            </div>
            {/* Phone CSS Mockup 4 */}
            <div className="relative border-gray-800 bg-[#0d0d0d] border-[8px] rounded-[2rem] h-[340px] w-[170px] shadow-lg flex-shrink-0">
              <div className="w-[70px] h-[14px] bg-gray-900 top-1.5 rounded-[0.8rem] left-1/2 -translate-x-1/2 absolute z-20" />
              <div className="rounded-[1.6rem] overflow-hidden w-full h-full p-2.5 pt-7 text-[7px] text-left flex flex-col justify-between bg-[#080808]">
                <p className="font-black text-white border-b border-white/5 pb-1.5">প্রশ্নব্যাংক ফিল্টার</p>
                <div className="space-y-1.5 flex-1 pt-2">
                  {['ঢাকা বোর্ড', 'রাজশাহী বোর্ড', 'কুমিল্লা বোর্ড', 'চট্টগ্রাম বোর্ড'].map((board, i) => (
                    <div key={i} className="p-1.5 bg-white/5 border border-white/5 rounded-lg flex items-center justify-between text-white font-medium">
                      <span>{board}</span>
                      <ChevronRight className="w-2.5 h-2.5 text-gray-500" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Feature 5: বন্ধুদের সাথে লড়াই */}
          <div className="bg-white/5 border border-white/5 hover:border-pink-500/20 p-6 rounded-3xl flex flex-col justify-between items-center gap-6 hover:shadow-xl hover:shadow-pink-500/5 transition-all group">
            <div className="text-center space-y-2">
              <span className="inline-flex items-center justify-center p-3 rounded-2xl bg-pink-500/10 text-pink-400">
                <Users className="w-6 h-6" />
              </span>
              <h4 className="font-extrabold text-base text-white pt-2">বন্ধুদের সাথে লড়াই ⚔️</h4>
              <p className="text-xs text-gray-400 font-normal leading-relaxed">
                অনলাইন বন্ধুদের চ্যালেঞ্জ পাঠিয়ে রিয়েল-টাইম কুইজ যুদ্ধ লড়ুন এবং জ্ঞান বাড়ান।
              </p>
            </div>
            {/* Phone CSS Mockup 5 */}
            <div className="relative border-gray-800 bg-[#0d0d0d] border-[8px] rounded-[2rem] h-[340px] w-[170px] shadow-lg flex-shrink-0">
              <div className="w-[70px] h-[14px] bg-gray-900 top-1.5 rounded-[0.8rem] left-1/2 -translate-x-1/2 absolute z-20" />
              <div className="rounded-[1.6rem] overflow-hidden w-full h-full p-2.5 pt-7 text-[7px] text-left flex flex-col justify-between bg-[#080808]">
                <div className="flex justify-between items-center border-b border-white/5 pb-1.5">
                  <span className="font-black text-white">কুইজ ব্যাটেল</span>
                  <span className="text-pink-500 font-black uppercase">LIVE VS</span>
                </div>
                <div className="flex-1 pt-3 flex flex-col items-center justify-center gap-3">
                  <div className="flex justify-around items-center w-full">
                    <div className="text-center space-y-1">
                      <div className="w-7 h-7 rounded-full bg-blue-500/20 text-[#00A3FF] border border-blue-500/30 flex items-center justify-center text-[8px] font-bold">রা</div>
                      <p className="text-[6px] text-white">রাহুল</p>
                      <p className="text-[7px] font-bold text-emerald-400">১২০ XP</p>
                    </div>
                    <span className="font-black text-[9px] text-gray-500">বনাম</span>
                    <div className="text-center space-y-1">
                      <div className="w-7 h-7 rounded-full bg-rose-500/20 text-rose-400 border border-rose-500/30 flex items-center justify-center text-[8px] font-bold">ইম</div>
                      <p className="text-[6px] text-white">ইমরান</p>
                      <p className="text-[7px] font-bold text-yellow-500">৯৫ XP</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Feature 6: লিডারবোর্ড */}
          <div className="bg-white/5 border border-white/5 hover:border-[#00CBA9]/20 p-6 rounded-3xl flex flex-col justify-between items-center gap-6 hover:shadow-xl hover:shadow-[#00CBA9]/5 transition-all group">
            <div className="text-center space-y-2">
              <span className="inline-flex items-center justify-center p-3 rounded-2xl bg-emerald-500/10 text-[#00CBA9]">
                <Trophy className="w-6 h-6" />
              </span>
              <h4 className="font-extrabold text-base text-white pt-2">গ্লোবাল লিডারবোর্ড 🏆</h4>
              <p className="text-xs text-gray-400 font-normal leading-relaxed">
                পয়েন্ট অর্জন করে আপনার স্কুল, জেলা ও সারাদেশের র‍্যাঙ্কিংয়ে শীর্ষে থাকুন।
              </p>
            </div>
            {/* Phone CSS Mockup 6 */}
            <div className="relative border-gray-800 bg-[#0d0d0d] border-[8px] rounded-[2rem] h-[340px] w-[170px] shadow-lg flex-shrink-0">
              <div className="w-[70px] h-[14px] bg-gray-900 top-1.5 rounded-[0.8rem] left-1/2 -translate-x-1/2 absolute z-20" />
              <div className="rounded-[1.6rem] overflow-hidden w-full h-full p-2.5 pt-7 text-[7px] text-left flex flex-col justify-between bg-[#080808]">
                <p className="font-black text-white border-b border-white/5 pb-1.5">লিডারবোর্ড টপ ৩</p>
                <div className="space-y-1.5 flex-1 pt-2">
                  {[
                    { r: 1, name: 'তাসনিম কবির', pt: '৪৮০০ XP', c: 'text-yellow-500' },
                    { r: 2, name: 'রাহুল আহমেদ', pt: '৪২০০ XP', c: 'text-slate-300' },
                    { r: 3, name: 'সাদিয়া তাসনিম', pt: '৩৮৫০ XP', c: 'text-amber-600' }
                  ].map((user, i) => (
                    <div key={i} className="p-1.5 bg-white/5 rounded-lg border border-white/5 flex items-center justify-between font-medium">
                      <div className="flex items-center gap-1.5">
                        <span className={`font-black ${user.c}`}>#{user.r}</span>
                        <span className="text-white">{user.name}</span>
                      </div>
                      <span className="text-emerald-400 font-bold">{user.pt}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>



      {/* 6. Testimonials Section ("চর্চার সাফল্য") */}
      <section id="success" className="py-24 bg-[#0a0a0a] border-t border-white/5 relative px-4 md:px-8">
        <div className="max-w-7xl mx-auto space-y-16">
          <div className="text-center space-y-4 max-w-xl mx-auto">
            <span className="text-purple-400 text-xs font-bold uppercase tracking-widest bg-purple-500/5 border border-purple-500/10 px-3 py-1 rounded-full">
              সফল শিক্ষার্থীদের মতামত
            </span>
            <h3 className="text-3xl md:text-5xl font-black text-white">চর্চার সাফল্যগাথা</h3>
            <p className="text-sm text-gray-400 font-normal">
              চর্চা অ্যাপ ব্যবহার করে পরীক্ষায় সফল ফলাফল নিয়ে এসেছে দেশজুড়ে হাজারো শিক্ষার্থী।
            </p>
          </div>

          {/* Testimonial card grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch text-left">
            {[
              {
                name: 'ফাহিম তাজওয়ার',
                desc: 'এসএসসি ২০২৫ ব্যাচ',
                review: 'চর্চা অ্যাপের অধ্যায় ভিত্তিক কুইজগুলো আমার পদার্থবিজ্ঞান ও রসায়নের জটিল অধ্যায়গুলোকে অনেক সহজ করে দিয়েছে। বিশেষ করে মক পরীক্ষা টাইমিং শিখতে সাহায্য করেছে।',
                rating: 5
              },
              {
                name: 'সানজিদা ইসলাম',
                desc: 'এইচএসসি ২০২৬ ব্যাচ',
                review: 'বোর্ড প্রশ্নপত্র সমাধান করার জন্য আর কোনো গাইড প্রয়োজন হয় না। টেস্ট পেপারের সব বোর্ড আর কলেজের প্রশ্নব্যাংক চর্চায় গোছানো আকারে থাকায় অনেক ভালো প্রস্তুতি হচ্ছে।',
                rating: 5
              },
              {
                name: 'আরাফাত চৌধুরী',
                desc: 'এসএসসি ২০২৫ ব্যাচ',
                review: 'বন্ধুদের সাথে কুইজ লড়াই ফিচারটি অসাধারণ! পড়ার ফাঁকে বন্ধুদের সাথে কুইজ খেলে প্র্যাকটিস করার কারণে আমার অনেক টপিক সহজেই আয়ত্তে চলে এসেছে।',
                rating: 5
              },
              {
                name: 'তানিয়া রহমান',
                desc: 'এসএসসি ২০২৬ ব্যাচ',
                review: 'সাপ্তাহিক উন্নতি গ্রাফ আর দুর্বল দিক সনাক্তকরণ অপশনটি চমৎকার। আমি কোন টপিকে কম স্কোর করছি তা দেখায়, ফলে সেই টপিকটি আবার পড়তে সুবিধা হয়।',
                rating: 5
              },
              {
                name: 'রাইহান উদ্দীন',
                desc: 'এইচএসসি ২০২৬ ব্যাচ',
                review: 'মক টেস্টে নেগেটিভ মার্কিং মোড থাকায় পরীক্ষার ভীতি দূর হয়েছে। লিডারবোর্ডে নিজেকে উপরে দেখার জন্য আরও বেশি পড়ার তাগিদ অনুভব করি।',
                rating: 5
              },
              {
                name: 'নুসরাত জাহান',
                desc: 'এসএসসি ২০২৫ ব্যাচ',
                review: 'মোবাইল দিয়ে পড়াশোনা যে এত ফান আর ইন্টারেক্টিভ হতে পারে চর্চা ব্যবহার না করলে বুঝতাম না। সম্পূর্ণ বিজ্ঞাপনমুক্ত এবং খুব স্মুথ একটি অ্যাপ্লিকেশন।',
                rating: 5
              }
            ].map((testi, idx) => (
              <motion.div
                key={idx}
                variants={fadeInUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="p-6 rounded-3xl bg-white/5 border border-white/5 flex flex-col justify-between gap-4"
              >
                <div className="flex gap-1">
                  {[...Array(testi.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  ))}
                </div>
                <p className="text-xs text-gray-300 font-normal leading-relaxed italic flex-1">
                  "{testi.review}"
                </p>
                <div className="flex items-center gap-3 pt-3 border-t border-white/5">
                  <div className="w-9 h-9 rounded-full bg-emerald-500/10 text-emerald-400 flex items-center justify-center font-bold text-xs">
                    {testi.name.substring(0, 2)}
                  </div>
                  <div>
                    <h5 className="font-extrabold text-xs text-white leading-none">{testi.name}</h5>
                    <span className="text-[10px] text-gray-500 mt-1 block">{testi.desc}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 6.5 Instructor CTA Section */}
      {(!window || !(window as any).shInstructorReg || ((window as any).shInstructorReg.become_instructor_btn_enabled !== 'no' && (window as any).shInstructorReg.become_instructor_btn_enabled !== false && (window as any).shInstructorReg.become_instructor_btn_enabled !== 'false')) && (
        <section className="py-16 px-4 md:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-emerald-900/40 via-teal-900/30 to-cyan-900/40 border border-emerald-500/20 p-8 md:p-10">
              <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-teal-500/5 rounded-full blur-3xl" />
              <div className="relative flex flex-col md:flex-row items-center gap-6 md:gap-10">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500/30 to-teal-500/30 border border-emerald-500/30 flex items-center justify-center shrink-0">
                  <GraduationCap className="w-8 h-8 text-emerald-400" />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-lg font-extrabold text-white mb-1">শিক্ষক হিসেবে যোগ দিন</h3>
                  <p className="text-xs text-gray-400 leading-relaxed max-w-lg">
                    শুদ্ধশিক্ষা প্ল্যাটফর্মে শিক্ষক হিসেবে যোগ দিন এবং হাজারো শিক্ষার্থীকে পড়ান। আপনার জ্ঞান শেয়ার করুন এবং আয় করুন।
                  </p>
                </div>
                <button
                  onClick={onBecomeInstructor}
                  className="shrink-0 px-6 py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white text-sm font-bold flex items-center gap-2 transition-all shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/30"
                >
                  <span>শিক্ষক হিসেবে যোগ দিন</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 7. Footer */}
      <footer id="contact" className="bg-[#050505] text-left border-t border-white/5 pt-20 pb-28 md:pb-16 px-4 md:px-8">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-12 pb-16 border-b border-white/5">
          {/* Column 1: Info */}
          <div className="lg:col-span-4 space-y-6">
            <div className="flex items-center gap-3">
              <img src={logo} alt="শুদ্ধশিক্ষা" className="w-10 h-10 rounded-lg object-cover" />
              <h4 className="text-base font-extrabold text-white leading-none">শুদ্ধশিক্ষা</h4>
            </div>
            <p className="text-xs text-gray-400 font-normal leading-relaxed max-w-sm">
              শুদ্ধশিক্ষা হচ্ছে বাংলাদেশের একটি উদীয়মান এডটেক প্ল্যাটফর্ম যা এসএসসি ও এইচএসসি পরীক্ষার্থীদের সহজ ও আধুনিক কুইজ চর্চার মাধ্যমে ১০০% গোছানো প্রস্তুতির নিশ্চয়তা দেয়।
            </p>
            <div className="flex items-center gap-3 pt-2">
              {['facebook', 'youtube', 'instagram', 'twitter'].map((social) => (
                <a key={social} href={`#${social}`} className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/15 transition-all text-gray-400 hover:text-white text-[10px] capitalize">
                  {social[0]}
                </a>
              ))}
            </div>
          </div>

          {/* Column 2: links */}
          <div className="lg:col-span-2 space-y-4">
            <h5 className="font-extrabold text-xs text-white uppercase tracking-wider">কোম্পানি</h5>
            <ul className="space-y-2.5 text-xs text-gray-500 font-medium">
              <li><a href="#about" className="hover:text-white transition-colors">আমাদের সম্পর্কে</a></li>
              <li><a href="#career" className="hover:text-white transition-colors">ক্যারিয়ার</a></li>
              <li><a href="#blog" className="hover:text-white transition-colors">ব্লগ পোস্ট</a></li>
              <li><a href="#press" className="hover:text-white transition-colors">প্রেস কিট</a></li>
            </ul>
          </div>

          {/* Column 3: links */}
          <div className="lg:col-span-2 space-y-4">
            <h5 className="font-extrabold text-xs text-white uppercase tracking-wider">ফিচারসমূহ</h5>
            <ul className="space-y-2.5 text-xs text-gray-500 font-medium">
              <li><a href="#features" className="hover:text-white transition-colors">অধ্যায় কুইজ</a></li>
              <li><a href="#features" className="hover:text-white transition-colors">মক পরীক্ষা</a></li>
              <li><a href="#features" className="hover:text-white transition-colors">পারফরম্যান্স বিশ্লেষণ</a></li>
              <li><a href="#features" className="hover:text-white transition-colors">কনটেস্ট যুদ্ধ</a></li>
            </ul>
          </div>

          {/* Column 4: support */}
          <div className="lg:col-span-2 space-y-4">
            <h5 className="font-extrabold text-xs text-white uppercase tracking-wider">সহায়তা</h5>
            <ul className="space-y-2.5 text-xs text-gray-500 font-medium">
              <li><a href="#help" className="hover:text-white transition-colors">হেল্প সেন্টার</a></li>
              <li><a href="#support" className="hover:text-white transition-colors">কমিউনিটি ফোরাম</a></li>
              <li><a href="#safety" className="hover:text-white transition-colors">সুরক্ষা গাইড</a></li>
              <li><a href="#contact" className="hover:text-white transition-colors">যোগাযোগ করুন</a></li>
            </ul>
          </div>

          {/* Column 5: support */}
          <div className="lg:col-span-2 space-y-4">
            <h5 className="font-extrabold text-xs text-white uppercase tracking-wider">আইনি তথ্য</h5>
            <ul className="space-y-2.5 text-xs text-gray-500 font-medium">
              <li><a href="#terms" className="hover:text-white transition-colors">শর্তাবলী</a></li>
              <li><a href="#privacy" className="hover:text-white transition-colors">গোপনীয়তা নীতি</a></li>
              <li><a href="#cookies" className="hover:text-white transition-colors">কুকিজ সেটিং</a></li>
              <li><a href="#refund" className="hover:text-white transition-colors">রিফান্ড পলিসি</a></li>
            </ul>
          </div>

        </div>

        {/* Footer Bottom copyright and store icons */}
        <div className="max-w-7xl mx-auto pt-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <p className="text-[10px] text-gray-600 font-medium">
            &copy; {new Date().getFullYear()} শুদ্ধশিক্ষা ডট নেট। সর্বস্বত্ব সংরক্ষিত।
          </p>
          <div className="flex items-center gap-3">
            <a href="#playstore" className="hover:opacity-85 transition-opacity">
              <div className="bg-white/5 border border-white/10 rounded-lg px-2.5 py-1 flex items-center gap-2 text-left w-24">
                <Smartphone className="w-3.5 h-3.5 text-emerald-400" />
                <div>
                  <p className="text-[5px] uppercase tracking-wider text-gray-500 font-bold leading-none">GET IT ON</p>
                  <p className="text-[8px] text-white font-bold leading-none mt-0.5 font-english">Google Play</p>
                </div>
              </div>
            </a>
            <a href="#appstore" className="hover:opacity-85 transition-opacity">
              <div className="bg-white/5 border border-white/10 rounded-lg px-2.5 py-1 flex items-center gap-2 text-left w-24">
                <Play className="w-3.5 h-3.5 text-[#00A3FF] fill-current" />
                <div>
                  <p className="text-[5px] uppercase tracking-wider text-gray-500 font-bold leading-none">Download on</p>
                  <p className="text-[8px] text-white font-bold leading-none mt-0.5 font-english">App Store</p>
                </div>
              </div>
            </a>
          </div>
        </div>
      </footer>

    </div>
  );
}
